import { isNil } from "lodash";
import { useRefreshBookingData } from "PFApp/booking/hooks/use_refresh_booking_data";
import { useSyncRows } from "PFApp/booking/parts/overview/use_sync_rows";
import { updateBooking as updateBookingRequest } from "PFApp/booking/services/api";
import { useGrowl } from "PFApp/use_growl";
import { useBookingInvalidate } from "PFCore/hooks/queries";
import { useProfileInvalidate } from "PFCore/hooks/queries/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Booking } from "PFTypes";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  booking: Booking;
  onError: () => void;
}

const useUpdateBooking = ({ booking, onError }: Props) => {
  const growl = useGrowl();
  const [loading, setLoading] = useState<boolean>();
  const syncRows = useSyncRows();
  const { utc, formatISODateTime } = useDateFormatter();
  const refreshBookingData = useRefreshBookingData();
  const { t } = useTranslation("bookingModule");
  const { invalidateProfileWithBooking } = useProfileInvalidate();
  const { invalidate: invalidateBooking } = useBookingInvalidate();

  const updateBooking = useCallback(
    async ({ startDate, endDate }) => {
      if (!booking) {
        return;
      }
      try {
        setLoading(true);
        // regular API call instead of react-query mutation hook to improve performance
        await updateBookingRequest(booking?.id, {
          start_date: formatISODateTime(utc(startDate)), // eslint-disable-line camelcase
          end_date: formatISODateTime(utc(endDate)) // eslint-disable-line camelcase
        });
        refreshBookingData({
          bookingIds: [booking?.id],
          bookingGroupId: booking?.booking_group_id,
          activityId: booking?.activity?.id,
          profileId: booking?.profile_id,
          refreshProfile: !isNil(booking.booking_template_id)
        });
        invalidateProfileWithBooking(booking.id);
        invalidateBooking(booking.id);
        const parentActivityIds = booking.activity?.parent_activity?.id
          ? [booking.activity.parent_activity.id]
          : [];
        await syncRows({
          profileId: booking.profile_id,
          activityId: booking.activity?.id,
          parentActivityIds
        });
        await new Promise<void>((resolve) =>
          setTimeout(() => {
            setLoading(false);
            growl({
              kind: "success",
              message: t("bookings.update.success")
            });
            resolve();
          }, 200)
        ); // give the chart some time to update;
      } catch (response) {
        setLoading(false);
        growl({
          kind: "error",
          message: t("bookings.update.error")
        });
        onError();
      }
    },
    [booking]
  );

  return { updateBooking, loading };
};

export default useUpdateBooking;
